import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Crown, Sparkles, Tag, EyeOff } from "lucide-react";
import api from "../lib/api";

const TIER_META = {
  "hyper-premium": {
    label: "Hyper-premium",
    icon: Crown,
    badge: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    accent: "text-amber-300",
  },
  premium: {
    label: "Premium",
    icon: Sparkles,
    badge: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
    accent: "text-fuchsia-300",
  },
  mainline: {
    label: "Mainline",
    icon: Tag,
    badge: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
    accent: "text-zinc-300",
  },
  unknown: {
    label: "Other",
    icon: Tag,
    badge: "bg-zinc-700/30 text-zinc-400 border-zinc-700",
    accent: "text-zinc-400",
  },
};

export default function BrandIndexPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tierFilter, setTierFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    api.get("/brands")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load brands"));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.brands.filter((b) => {
      if (activeOnly && !b.release_count) return false;
      if (tierFilter !== "all" && b.tier !== tierFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!b.canonical.toLowerCase().includes(q) && !b.notes.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data, tierFilter, search, activeOnly]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-rose-400" data-testid="brand-index-error">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="brand-index-loading">
        <Loader2 className="w-10 h-10 text-zinc-400 animate-spin" />
      </div>
    );
  }

  const tierCounts = data.tier_counts || {};
  const totals = data.totals || { release_count: 0, owner_count: 0, active_brand_count: 0 };
  const dormantCount = (data.total || 0) - (totals.active_brand_count || 0);

  return (
    <div className="min-h-screen text-white" data-testid="brand-index-page" style={{
      background: "radial-gradient(circle at 70% -10%, rgba(244,114,182,0.1) 0%, transparent 50%), linear-gradient(160deg,#06060a 0%,#0a0814 60%,#15081e 100%)",
    }}>
      {/* Header */}
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-white/5">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-index-home">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-lg" style={{ background: "linear-gradient(135deg,#f43f5e,#a855f7)" }}>C</div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-tight text-base">Castfolio</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Diecast portfolio</div>
          </div>
        </Link>
        <Link to="/login" className="text-xs sm:text-sm text-zinc-400 hover:text-white" data-testid="brand-index-login">
          Sign in →
        </Link>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Hero */}
        <div className="max-w-3xl">
          <p className="text-amber-300 text-xs uppercase tracking-[0.4em] font-semibold">Brand index</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-3 leading-[1.05] tracking-tight"
              style={{ background: "linear-gradient(135deg,#fff 0%,#fbcfe8 60%,#f0abfc 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
            Every brand we track
          </h1>
          <p className="text-zinc-400 mt-4 text-base sm:text-lg leading-relaxed">
            {data.total} diecast brands in the Castfolio universe — from Hot Wheels mainline to PGM resin grails.
            Tier badges + typical MSRP bands + how many of our collectors own each.
          </p>

          {/* Rollup pills */}
          <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="brand-index-rollup">
            <RollupPill label="active brands" value={totals.active_brand_count} accent="text-amber-200" />
            <RollupPill label="releases tracked" value={totals.release_count} accent="text-fuchsia-200" />
            <RollupPill label="collectors" value={totals.owner_count} accent="text-rose-200" />
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sticky top-0 z-10 bg-zinc-950/80 backdrop-blur-md py-3 -mx-2 px-2 rounded-xl">
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="brand-tier-filters">
            <TierChip active={tierFilter === "all"} onClick={() => setTierFilter("all")} testId="brand-tier-all">
              All <span className="opacity-60 ml-1">{data.total}</span>
            </TierChip>
            {Object.entries(TIER_META).map(([key, meta]) => {
              const count = tierCounts[key] || 0;
              if (!count) return null;
              return (
                <TierChip key={key} active={tierFilter === key} onClick={() => setTierFilter(key)} testId={`brand-tier-${key}`}>
                  {meta.label} <span className="opacity-60 ml-1">{count}</span>
                </TierChip>
              );
            })}
            {dormantCount > 0 && (
              <button
                type="button"
                onClick={() => setActiveOnly((v) => !v)}
                data-testid="brand-active-toggle"
                title={activeOnly ? "Show brands with no releases yet" : "Hide brands with no releases yet"}
                className={`ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  activeOnly
                    ? "bg-amber-500/15 border-amber-400/50 text-amber-200 hover:bg-amber-500/25"
                    : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-200"
                }`}
              >
                <EyeOff className="w-3 h-3" />
                {activeOnly ? `Active only (${dormantCount} hidden)` : `Showing all ${data.total}`}
              </button>
            )}
          </div>
          <div className="ml-auto relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="brand-search-input"
              type="text"
              placeholder="Search brand or note…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-zinc-900/80 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-fuchsia-500/40"
            />
          </div>
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="mt-16 text-center text-zinc-500" data-testid="brand-index-empty">
            {activeOnly && search.trim() === "" && tierFilter === "all" ? (
              <>
                <p>No brands have releases yet.</p>
                <button
                  type="button"
                  onClick={() => setActiveOnly(false)}
                  className="mt-3 text-amber-300 hover:text-amber-200 text-sm font-semibold"
                  data-testid="brand-index-show-all"
                >
                  Show all {data.total} tracked brands →
                </button>
              </>
            ) : (
              "No brands match this filter."
            )}
          </div>
        )}

        {/* Brand grid */}
        <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brand-index-grid">
          {filtered.map((b) => (
            <BrandCard key={b.slug} brand={b} />
          ))}
        </ul>

        {/* Footer */}
        <p className="text-zinc-500 text-xs mt-16 text-center">
          Pricing bands are typical secondary-market estimates aggregated across our collector base. Variations are expected.
        </p>
      </main>
    </div>
  );
}

function RollupPill({ label, value, accent }) {
  return (
    <div className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
      <span className={`font-mono font-bold text-sm ${accent}`}>{(value || 0).toLocaleString()}</span>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    </div>
  );
}

function TierChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
        active
          ? "bg-rose-500/20 border-rose-400/60 text-rose-200 shadow-[0_0_14px_rgba(244,63,94,0.25)]"
          : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-rose-500/30 hover:text-rose-300"
      }`}
    >
      {children}
    </button>
  );
}

function BrandCard({ brand }) {
  const meta = TIER_META[brand.tier] || TIER_META.unknown;
  const Icon = meta.icon;
  const msrp =
    brand.msrp_low && brand.msrp_high
      ? `$${brand.msrp_low.toFixed(0)}–$${brand.msrp_high.toFixed(0)}`
      : "MSRP unknown";
  const isDormant = !brand.release_count;

  return (
    <li>
      <Link
        to={`/brands/${brand.slug}`}
        data-testid={`brand-card-${brand.slug}`}
        className={`block rounded-2xl ring-1 ring-white/5 hover:ring-fuchsia-500/30 bg-zinc-900/60 hover:bg-zinc-900/90 p-5 h-full transition group ${
          isDormant ? "opacity-55 hover:opacity-90" : ""
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold tracking-tight text-white truncate">{brand.canonical}</h3>
            <p className={`text-[10px] uppercase tracking-[0.25em] mt-1 ${meta.accent} flex items-center gap-1.5`}>
              <Icon className="w-3 h-3" /> {meta.label}
              {isDormant && (
                <span className="ml-1 text-[9px] uppercase tracking-wider text-zinc-500 normal-case font-medium">· awaiting first release</span>
              )}
            </p>
          </div>
          <span className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${meta.badge}`}>
            {msrp}
          </span>
        </div>
        <p className="text-xs text-zinc-400 mt-3 leading-relaxed line-clamp-3 min-h-[3lh]">{brand.notes || "No notes available."}</p>
        <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between gap-2 text-[11px]">
          {isDormant ? (
            <span className="text-zinc-500 italic">No tracked releases yet.</span>
          ) : (
            <>
              <span className="text-zinc-500">
                <span className="text-zinc-200 font-bold">{brand.release_count.toLocaleString()}</span> releases
              </span>
              <span className="text-zinc-500">
                <span className="text-zinc-200 font-bold">{brand.owner_count}</span> {brand.owner_count === 1 ? "collector" : "collectors"}
              </span>
              {brand.avg_market_value != null && (
                <span className="text-zinc-500">
                  avg <span className={`font-mono font-bold ${meta.accent}`}>${Math.round(brand.avg_market_value)}</span>
                </span>
              )}
            </>
          )}
        </div>
      </Link>
    </li>
  );
}
